import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

/*
|--------------------------------------------------------------------------
| LOCAL SCHOOL LOGO
|--------------------------------------------------------------------------
| Put your logo here:
|
| src/assets/school-logo.png
|
| If your ID card component is located somewhere else,
| adjust the "../assets/" path accordingly.
|--------------------------------------------------------------------------
*/

import schoolLogo from "./savationChristLogo.png";

/*
|--------------------------------------------------------------------------
| SCHOOL COLORS
|--------------------------------------------------------------------------
*/

const COLORS = {
  purple: "#4B145F",
  deepPurple: "#32103F",
  gold: "#D9A400",
  brightGold: "#F2C94C",
  black: "#111111",
  darkGray: "#2B2B2B",
  gray: "#666666",
  lightGray: "#F3F3F3",
  softGold: "#FFF8DD",
  softPurple: "#F5EFF8",
  white: "#FFFFFF",
  border: "#D9A400",
};

/*
|--------------------------------------------------------------------------
| STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({

  /*
  |--------------------------------------------------------------------------
  | PAGE
  |--------------------------------------------------------------------------
  */

  page: {
    size: "A4",
    orientation: "landscape",
    padding: 18,
    backgroundColor: COLORS.white,
  },

  grid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "space-between",
    height: "100%",
  },

  cardWrapper: {
    width: "31.7%",
    height: "31.2%",
    marginBottom: 8,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT CARD
  |--------------------------------------------------------------------------
  */

  card: {
    width: "100%",
    height: "100%",
    border: `1.5pt solid ${COLORS.purple}`,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  /*
  |--------------------------------------------------------------------------
  | DECORATION
  |--------------------------------------------------------------------------
  */

  topGoldBar: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: 5,
    backgroundColor: COLORS.gold,
  },

  topPurpleBar: {
    position: "absolute",
    top: 5,
    left: 0,
    width: "100%",
    height: 3,
    backgroundColor: COLORS.purple,
  },

  bottomPurpleBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: 5,
    backgroundColor: COLORS.purple,
  },

  bottomGoldBar: {
    position: "absolute",
    bottom: 5,
    left: 0,
    width: "100%",
    height: 2,
    backgroundColor: COLORS.gold,
  },

  cornerGold: {
    position: "absolute",
    width: 55,
    height: 55,
    borderRadius: 28,
    backgroundColor: COLORS.gold,
    opacity: 0.08,
    top: -28,
    right: -20,
  },

  cornerPurple: {
    position: "absolute",
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: COLORS.purple,
    opacity: 0.06,
    bottom: -35,
    left: -30,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT HEADER
  |--------------------------------------------------------------------------
  */

  header: {
    height: 47,
    backgroundColor: COLORS.purple,
    paddingHorizontal: 7,
    paddingVertical: 4,
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
  },

  /*
  |--------------------------------------------------------------------------
  | LOGO
  |--------------------------------------------------------------------------
  */

  logoContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
    marginRight: 7,
    border: `1pt solid ${COLORS.gold}`,
  },

  schoolLogo: {
    width: 27,
    height: 27,
    objectFit: "contain",
  },

  /*
  |--------------------------------------------------------------------------
  | HEADER TEXT
  |--------------------------------------------------------------------------
  */

  headerText: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingRight: 3,
  },

  schoolName: {
    color: COLORS.brightGold,
    fontSize: 7.8,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  schoolAddress: {
    color: COLORS.white,
    fontSize: 4.1,
    marginTop: 2,
    textAlign: "center",
    fontWeight: "bold",
  },

  schoolSubtitle: {
    color: COLORS.white,
    fontSize: 4.9,
    marginTop: 2.5,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 0.35,
  },

  headerGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.gold,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT BODY
  |--------------------------------------------------------------------------
  */

  cardBody: {
    flexDirection: "row",
    padding: 7,
    paddingTop: 8,
    flex: 1,
  },

  /*
  |--------------------------------------------------------------------------
  | PHOTO
  |--------------------------------------------------------------------------
  */

  imageSection: {
    width: 67,
    alignItems: "center",
    justifyContent: "center",
  },

  imageContainer: {
    width: 62,
    height: 76,
    border: `1.5pt solid ${COLORS.gold}`,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: COLORS.lightGray,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },

  imageTopAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: COLORS.purple,
  },

  studentImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  noImage: {
    fontSize: 5.5,
    color: COLORS.gray,
    fontWeight: "bold",
    textAlign: "center",
  },

  photoLabel: {
    marginTop: 3,
    fontSize: 4.2,
    color: COLORS.purple,
    fontWeight: "bold",
    textAlign: "center",
  },

  /*
  |--------------------------------------------------------------------------
  | STUDENT DETAILS
  |--------------------------------------------------------------------------
  */

  details: {
    flex: 1,
    marginLeft: 7,
    justifyContent: "center",
  },

  studentIdBox: {
    backgroundColor: COLORS.softGold,
    borderLeft: `3pt solid ${COLORS.gold}`,
    paddingVertical: 3,
    paddingHorizontal: 5,
    marginBottom: 5,
    borderRadius: 2,
  },

  studentIdLabel: {
    fontSize: 4.2,
    color: COLORS.purple,
    fontWeight: "bold",
    marginBottom: 1,
  },

  studentId: {
    fontSize: 7.4,
    color: COLORS.black,
    fontWeight: "bold",
  },

  detailRow: {
    flexDirection: "row",
    marginBottom: 3.5,
    alignItems: "flex-start",
  },

  label: {
    width: 45,
    fontSize: 5.1,
    fontWeight: "bold",
    color: COLORS.purple,
  },

  value: {
    flex: 1,
    fontSize: 6.2,
    color: COLORS.black,
    fontWeight: "bold",
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT FOOTER
  |--------------------------------------------------------------------------
  */

  footer: {
    minHeight: 18,
    backgroundColor: COLORS.softPurple,
    borderTop: `1pt solid ${COLORS.purple}`,
    paddingHorizontal: 6,
    paddingVertical: 3,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
  },

  footerText: {
    maxWidth: "58%",
    fontSize: 4.1,
    color: COLORS.purple,
    fontWeight: "bold",
  },

  footerYear: {
    maxWidth: "38%",
    fontSize: 4.1,
    color: COLORS.black,
    fontWeight: "bold",
    textAlign: "right",
  },

  footerGoldAccent: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "32%",
    height: 2,
    backgroundColor: COLORS.gold,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK CARD
  |--------------------------------------------------------------------------
  */

  backCard: {
    width: "100%",
    height: "100%",
    border: `1.5pt solid ${COLORS.purple}`,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  /*
  |--------------------------------------------------------------------------
  | BACK HEADER
  |--------------------------------------------------------------------------
  */

  backTop: {
    height: 46,
    backgroundColor: COLORS.purple,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 7,
    position: "relative",
  },

  backLogoContainer: {
    width: 31,
    height: 31,
    borderRadius: 16,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
    marginRight: 7,
    border: `1pt solid ${COLORS.gold}`,
  },

  backLogo: {
    width: 26,
    height: 26,
    objectFit: "contain",
  },

  backHeaderText: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  backTitle: {
    color: COLORS.brightGold,
    fontSize: 7.5,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
  },

  backAddress: {
    color: COLORS.white,
    fontSize: 4.3,
    marginTop: 2,
    fontWeight: "bold",
    textAlign: "center",
  },

  backSubtitle: {
    color: COLORS.white,
    fontSize: 4.8,
    marginTop: 2.5,
    fontWeight: "bold",
    textAlign: "center",
  },

  backGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.gold,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK CONTENT
  |--------------------------------------------------------------------------
  */

  backContent: {
    flex: 1,
    padding: 7,
    justifyContent: "space-between",
  },

  instructionBox: {
    backgroundColor: COLORS.softGold,
    border: `0.8pt solid ${COLORS.gold}`,
    borderRadius: 5,
    padding: 5,
  },

  instructionHeading: {
    color: COLORS.purple,
    fontSize: 5.6,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 3,
  },

  instructions: {
    fontSize: 5.2,
    lineHeight: 1.35,
    color: COLORS.black,
    textAlign: "center",
    fontWeight: "bold",
  },

  /*
  |--------------------------------------------------------------------------
  | RULES
  |--------------------------------------------------------------------------
  */

  rulesBox: {
    border: `1pt solid ${COLORS.purple}`,
    padding: 5,
    marginTop: 5,
    borderRadius: 5,
    backgroundColor: COLORS.softPurple,
  },

  rulesHeading: {
    fontSize: 5.5,
    color: COLORS.purple,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 3,
  },

  rule: {
    fontSize: 4.8,
    color: COLORS.black,
    marginBottom: 2.2,
    lineHeight: 1.2,
    fontWeight: "bold",
  },

  /*
  |--------------------------------------------------------------------------
  | EMERGENCY
  |--------------------------------------------------------------------------
  */

  emergencyBox: {
    marginTop: 5,
    backgroundColor: COLORS.purple,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 5,
    alignItems: "center",
  },

  emergency: {
    textAlign: "center",
    fontSize: 5.2,
    fontWeight: "bold",
    color: COLORS.brightGold,
  },

  emergencySubtext: {
    textAlign: "center",
    fontSize: 4.4,
    color: COLORS.white,
    marginTop: 1,
    fontWeight: "bold",
  },

  /*
  |--------------------------------------------------------------------------
  | SIGNATURES
  |--------------------------------------------------------------------------
  */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 6,
  },

  signatureBlock: {
    width: "42%",
    alignItems: "center",
  },

  signature: {
    width: "100%",
    borderTop: `1pt solid ${COLORS.purple}`,
    paddingTop: 2,
    textAlign: "center",
    fontSize: 4.5,
    color: COLORS.purple,
    fontWeight: "bold",
  },

  /*
  |--------------------------------------------------------------------------
  | BACK FOOTER
  |--------------------------------------------------------------------------
  */

  backFooter: {
    marginTop: 5,
    paddingTop: 4,
    borderTop: `1pt solid ${COLORS.gold}`,
    alignItems: "center",
  },

  backFooterText: {
    fontSize: 4.4,
    color: COLORS.black,
    fontWeight: "bold",
    textAlign: "center",
  },
});

/*
|--------------------------------------------------------------------------
| IMAGE HANDLER
|--------------------------------------------------------------------------
*/

const getImageUrl = (image) => {
  if (!image) {
    return null;
  }

  return image;
};

/*
|--------------------------------------------------------------------------
| FRONT CARD
|--------------------------------------------------------------------------
*/

const StudentFrontCard = ({ student }) => {
  return (
    <View style={styles.card}>

      <View style={styles.topGoldBar} />
      <View style={styles.topPurpleBar} />
      <View style={styles.bottomPurpleBar} />
      <View style={styles.bottomGoldBar} />

      <View style={styles.cornerGold} />
      <View style={styles.cornerPurple} />

      {/* HEADER */}

      <View style={styles.header}>

        <View style={styles.logoContainer}>
          <Image
            src={schoolLogo}
            style={styles.schoolLogo}
          />
        </View>

        <View style={styles.headerText}>

          <Text style={styles.schoolName}>
            SALVATION IN CHRIST SCHOOL SYSTEM
          </Text>

          <Text style={styles.schoolAddress}>
            1st Cowfield Junction, Paynesville City, Monrovia, Liberia
          </Text>

          <Text style={styles.schoolSubtitle}>
            STUDENT IDENTIFICATION CARD
          </Text>

        </View>

        <View style={styles.headerGoldLine} />

      </View>

      {/* BODY */}

      <View style={styles.cardBody}>

        {/* PHOTO */}

        <View style={styles.imageSection}>

          <View style={styles.imageContainer}>

            <View style={styles.imageTopAccent} />

            {getImageUrl(student.student_image) ? (

              <Image
                src={getImageUrl(student.student_image)}
                style={styles.studentImage}
              />

            ) : (

              <Text style={styles.noImage}>
                NO PHOTO
              </Text>

            )}

          </View>

          <Text style={styles.photoLabel}>
            STUDENT
          </Text>

        </View>

        {/* DETAILS */}

        <View style={styles.details}>

          <View style={styles.studentIdBox}>

            <Text style={styles.studentIdLabel}>
              STUDENT IDENTIFICATION NUMBER
            </Text>

            <Text style={styles.studentId}>
              {student.card_number || "STUDENT ID"}
            </Text>

          </View>

          <View style={styles.detailRow}>

            <Text style={styles.label}>
              NAME:
            </Text>

            <Text style={styles.value}>
              {student.student_name || "N/A"}
            </Text>

          </View>

          <View style={styles.detailRow}>

            <Text style={styles.label}>
              CLASS:
            </Text>

            <Text style={styles.value}>
              {student.grade_class || "N/A"}
            </Text>

          </View>

          <View style={styles.detailRow}>

            <Text style={styles.label}>
              DIVISION:
            </Text>

            <Text style={styles.value}>
              {student.division || "N/A"}
            </Text>

          </View>

          <View style={styles.detailRow}>

            <Text style={styles.label}>
              GENDER:
            </Text>

            <Text style={styles.value}>
              {student.gender || "N/A"}
            </Text>

          </View>

          <View style={styles.detailRow}>

            <Text style={styles.label}>
              YEAR:
            </Text>

            <Text style={styles.value}>
              {student.academic_year_name || "N/A"}
            </Text>

          </View>

        </View>

      </View>

      {/* FOOTER */}

      <View style={styles.footer}>

        <Text style={styles.footerText}>
          PROPERTY OF SALVATION IN CHRIST SCHOOL SYSTEM
        </Text>

        <Text style={styles.footerYear}>
          VALID SCHOOL YEAR
        </Text>

        <View style={styles.footerGoldAccent} />

      </View>

    </View>
  );
};

/*
|--------------------------------------------------------------------------
| CREATE NINE CARD SLOTS
|--------------------------------------------------------------------------
*/

const createNineSlots = (students) => {

  const slots = [...students];

  while (slots.length < 9) {
    slots.push(null);
  }

  return slots.slice(0, 9);
};

/*
|--------------------------------------------------------------------------
| BACK CARD
|--------------------------------------------------------------------------
*/

const StudentBackCard = ({ student }) => {

  return (
    <View style={styles.backCard}>

      <View style={styles.topGoldBar} />
      <View style={styles.topPurpleBar} />

      <View style={styles.cornerGold} />
      <View style={styles.cornerPurple} />

      {/* BACK HEADER */}

      <View style={styles.backTop}>

        <View style={styles.backLogoContainer}>

          <Image
            src={schoolLogo}
            style={styles.backLogo}
          />

        </View>

        <View style={styles.backHeaderText}>

          <Text style={styles.backTitle}>
            SALVATION IN CHRIST SCHOOL SYSTEM
          </Text>

          <Text style={styles.backAddress}>
            1st Cowfield Junction, Paynesville City, Monrovia, Liberia
          </Text>

          <Text style={styles.backSubtitle}>
            STUDENT IDENTIFICATION CARD
          </Text>

        </View>

        <View style={styles.backGoldLine} />

      </View>

      {/* BACK CONTENT */}

      <View style={styles.backContent}>

        {/* IMPORTANT NOTICE */}

        <View style={styles.instructionBox}>

          <Text style={styles.instructionHeading}>
            IMPORTANT NOTICE
          </Text>

          <Text style={styles.instructions}>
            This identification card remains the property of
            Salvation In Christ School System. If found, please
            return it to the school administration or the nearest
            Police Station.
          </Text>

        </View>

        {/* RULES */}

        <View style={styles.rulesBox}>

          <Text style={styles.rulesHeading}>
            CARD REGULATIONS
          </Text>

          <Text style={styles.rule}>
            • This card must be presented when requested by school officials.
          </Text>

          <Text style={styles.rule}>
            • This card is valid only for the academic year shown on the front.
          </Text>

          <Text style={styles.rule}>
            • Do not lend this card to another person.
          </Text>

          <Text style={styles.rule}>
            • Report lost or damaged cards immediately to the school administration.
          </Text>

        </View>

        {/* EMERGENCY */}

        <View style={styles.emergencyBox}>

          <Text style={styles.emergency}>
            EMERGENCY CONTACT
          </Text>

          <Text style={styles.emergencySubtext}>
            SCHOOL ADMINISTRATION
          </Text>

        </View>

        {/* SIGNATURE */}

        <View style={styles.signatureSection}>

          <View style={styles.signatureBlock}>

            <Text style={styles.signature}>
              AUTHORIZED SIGNATURE
            </Text>

          </View>

          <View style={styles.signatureBlock}>

            <Text style={styles.signature}>
              SCHOOL STAMP
            </Text>

          </View>

        </View>

        {/* FOOTER */}

        <View style={styles.backFooter}>

          <Text style={styles.backFooterText}>
            CHRIST-CENTERED EDUCATION • CHARACTER • EXCELLENCE • SERVICE
          </Text>

        </View>

      </View>

    </View>
  );
};

/*
|--------------------------------------------------------------------------
| MAIN PDF COMPONENT
|--------------------------------------------------------------------------
*/

const StudentIDCardPDF = ({
  students = [],
  academicYear = null,
}) => {

  const pages = [];

  for (
    let index = 0;
    index < students.length;
    index += 9
  ) {

    pages.push(
      students.slice(index, index + 9)
    );

  }

  return (
    <Document
      title="Student Identification Cards"
      author="Salvation In Christ School System"
      subject="Student Identification Cards"
    >

      {pages.map((pageStudents, pageIndex) => {

        const slots = createNineSlots(pageStudents);

        return (
          <React.Fragment
            key={`card-sheet-${pageIndex}`}
          >

            {/* FRONT */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >

              <View style={styles.grid}>

                {slots.map((student, index) => (

                  <View
                    key={`front-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >

                    {student && (
                      <StudentFrontCard
                        student={student}
                      />
                    )}

                  </View>

                ))}

              </View>

            </Page>

            {/* BACK */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >

              <View style={styles.grid}>

                {slots.map((student, index) => (

                  <View
                    key={`back-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >

                    {student && (
                      <StudentBackCard
                        student={student}
                      />
                    )}

                  </View>

                ))}

              </View>

            </Page>

          </React.Fragment>
        );

      })}

    </Document>
  );
};

export default StudentIDCardPDF;




// Keep to See
//  import React from "react";

// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
// } from "@react-pdf/renderer";

// /* =========================================================
//    SCHOOL COLORS
// ========================================================= */

// const COLORS = {
//   purple: "#4B145F",
//   deepPurple: "#32103F",
//   gold: "#D9A400",
//   brightGold: "#F2C94C",
//   black: "#111111",
//   darkGray: "#2B2B2B",
//   gray: "#666666",
//   lightGray: "#F3F3F3",
//   softGold: "#FFF8DD",
//   softPurple: "#F5EFF8",
//   white: "#FFFFFF",
//   border: "#D9A400",
// };

// /* =========================================================
//    PDF STYLES
// ========================================================= */

// const styles = StyleSheet.create({
//   /* -------------------------------------------------------
//      PAGE
//   ------------------------------------------------------- */

//   page: {
//     size: "A4",
//     orientation: "landscape",
//     padding: 18,
//     backgroundColor: COLORS.white,
//   },

//   grid: {
//     display: "flex",
//     flexDirection: "row",
//     flexWrap: "wrap",
//     justifyContent: "space-between",
//     alignContent: "space-between",
//     height: "100%",
//   },

//   cardWrapper: {
//     width: "31.7%",
//     height: "31.2%",
//     marginBottom: 8,
//   },

//   /* -------------------------------------------------------
//      FRONT CARD
//   ------------------------------------------------------- */

//   card: {
//     width: "100%",
//     height: "100%",
//     border: `1.5pt solid ${COLORS.purple}`,
//     borderRadius: 8,
//     overflow: "hidden",
//     backgroundColor: COLORS.white,
//     position: "relative",
//   },

//   /* -------------------------------------------------------
//      DECORATIVE ELEMENTS
//   ------------------------------------------------------- */

//   topGoldBar: {
//     position: "absolute",
//     top: 0,
//     left: 0,
//     width: "100%",
//     height: 5,
//     backgroundColor: COLORS.gold,
//   },

//   topPurpleBar: {
//     position: "absolute",
//     top: 5,
//     left: 0,
//     width: "100%",
//     height: 3,
//     backgroundColor: COLORS.purple,
//   },

//   bottomPurpleBar: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     width: "100%",
//     height: 5,
//     backgroundColor: COLORS.purple,
//   },

//   bottomGoldBar: {
//     position: "absolute",
//     bottom: 5,
//     left: 0,
//     width: "100%",
//     height: 2,
//     backgroundColor: COLORS.gold,
//   },

//   cornerGold: {
//     position: "absolute",
//     width: 55,
//     height: 55,
//     borderRadius: 28,
//     backgroundColor: COLORS.gold,
//     opacity: 0.08,
//     top: -28,
//     right: -20,
//   },

//   cornerPurple: {
//     position: "absolute",
//     width: 70,
//     height: 70,
//     borderRadius: 35,
//     backgroundColor: COLORS.purple,
//     opacity: 0.06,
//     bottom: -35,
//     left: -30,
//   },

//   /* -------------------------------------------------------
//      FRONT HEADER
//   ------------------------------------------------------- */

//   header: {
//     height: 44,
//     backgroundColor: COLORS.purple,
//     paddingHorizontal: 8,
//     paddingTop: 7,
//     paddingBottom: 5,
//     justifyContent: "center",
//     alignItems: "center",
//     position: "relative",
//   },

//   headerGoldLine: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: COLORS.gold,
//   },

//   schoolName: {
//     color: COLORS.brightGold,
//     fontSize: 8.2,
//     fontWeight: "bold",
//     textAlign: "center",
//     textTransform: "uppercase",
//     letterSpacing: 0.25,
//   },

//   schoolAddress: {
//     color: COLORS.white,
//     fontSize: 4.5,
//     marginTop: 2,
//     textAlign: "center",
//     fontWeight: "bold",
//   },

//   schoolSubtitle: {
//     color: COLORS.white,
//     fontSize: 5.2,
//     marginTop: 3,
//     fontWeight: "bold",
//     textAlign: "center",
//     letterSpacing: 0.4,
//   },

//   /* -------------------------------------------------------
//      FRONT BODY
//   ------------------------------------------------------- */

//   cardBody: {
//     flexDirection: "row",
//     padding: 7,
//     paddingTop: 8,
//     flex: 1,
//   },

//   imageSection: {
//     width: 67,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   imageContainer: {
//     width: 62,
//     height: 76,
//     border: `1.5pt solid ${COLORS.gold}`,
//     borderRadius: 6,
//     overflow: "hidden",
//     backgroundColor: COLORS.lightGray,
//     alignItems: "center",
//     justifyContent: "center",
//     position: "relative",
//   },

//   imageTopAccent: {
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: 4,
//     backgroundColor: COLORS.purple,
//   },

//   studentImage: {
//     width: "100%",
//     height: "100%",
//     objectFit: "cover",
//   },

//   noImage: {
//     fontSize: 5.5,
//     color: COLORS.gray,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   photoLabel: {
//     marginTop: 3,
//     fontSize: 4.2,
//     color: COLORS.purple,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   /* -------------------------------------------------------
//      STUDENT DETAILS
//   ------------------------------------------------------- */

//   details: {
//     flex: 1,
//     marginLeft: 7,
//     justifyContent: "center",
//   },

//   studentIdBox: {
//     backgroundColor: COLORS.softGold,
//     borderLeft: `3pt solid ${COLORS.gold}`,
//     paddingVertical: 3,
//     paddingHorizontal: 5,
//     marginBottom: 5,
//     borderRadius: 2,
//   },

//   studentIdLabel: {
//     fontSize: 4.2,
//     color: COLORS.purple,
//     fontWeight: "bold",
//     marginBottom: 1,
//   },

//   studentId: {
//     fontSize: 7.4,
//     color: COLORS.black,
//     fontWeight: "bold",
//   },

//   detailRow: {
//     flexDirection: "row",
//     marginBottom: 3.5,
//     alignItems: "flex-start",
//   },

//   label: {
//     width: 45,
//     fontSize: 5.1,
//     fontWeight: "bold",
//     color: COLORS.purple,
//   },

//   value: {
//     flex: 1,
//     fontSize: 6.2,
//     color: COLORS.black,
//     fontWeight: "bold",
//   },

//   /* -------------------------------------------------------
//      FRONT FOOTER
//   ------------------------------------------------------- */

//   footer: {
//     minHeight: 18,
//     backgroundColor: COLORS.softPurple,
//     borderTop: `1pt solid ${COLORS.purple}`,
//     paddingHorizontal: 6,
//     paddingVertical: 3,
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//     position: "relative",
//   },

//   footerText: {
//     maxWidth: "58%",
//     fontSize: 4.1,
//     color: COLORS.purple,
//     fontWeight: "bold",
//   },

//   footerYear: {
//     maxWidth: "38%",
//     fontSize: 4.1,
//     color: COLORS.black,
//     fontWeight: "bold",
//     textAlign: "right",
//   },

//   footerGoldAccent: {
//     position: "absolute",
//     left: 0,
//     bottom: 0,
//     width: "32%",
//     height: 2,
//     backgroundColor: COLORS.gold,
//   },

//   /* =======================================================
//      BACK CARD
//   ======================================================= */

//   backCard: {
//     width: "100%",
//     height: "100%",
//     border: `1.5pt solid ${COLORS.purple}`,
//     borderRadius: 8,
//     overflow: "hidden",
//     backgroundColor: COLORS.white,
//     position: "relative",
//   },

//   backTop: {
//     height: 43,
//     backgroundColor: COLORS.purple,
//     alignItems: "center",
//     justifyContent: "center",
//     paddingHorizontal: 8,
//     position: "relative",
//   },

//   backGoldLine: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: COLORS.gold,
//   },

//   backTitle: {
//     color: COLORS.brightGold,
//     fontSize: 7.8,
//     fontWeight: "bold",
//     textAlign: "center",
//     textTransform: "uppercase",
//   },

//   backAddress: {
//     color: COLORS.white,
//     fontSize: 4.5,
//     marginTop: 2,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   backSubtitle: {
//     color: COLORS.white,
//     fontSize: 5,
//     marginTop: 3,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   /* -------------------------------------------------------
//      BACK CONTENT
//   ------------------------------------------------------- */

//   backContent: {
//     flex: 1,
//     padding: 7,
//     justifyContent: "space-between",
//   },

//   instructionBox: {
//     backgroundColor: COLORS.softGold,
//     border: `0.8pt solid ${COLORS.gold}`,
//     borderRadius: 5,
//     padding: 5,
//   },

//   instructionHeading: {
//     color: COLORS.purple,
//     fontSize: 5.6,
//     fontWeight: "bold",
//     textAlign: "center",
//     marginBottom: 3,
//   },

//   instructions: {
//     fontSize: 5.2,
//     lineHeight: 1.35,
//     color: COLORS.black,
//     textAlign: "center",
//     fontWeight: "bold",
//   },

//   /* -------------------------------------------------------
//      RULES
//   ------------------------------------------------------- */

//   rulesBox: {
//     border: `1pt solid ${COLORS.purple}`,
//     padding: 5,
//     marginTop: 5,
//     borderRadius: 5,
//     backgroundColor: COLORS.softPurple,
//   },

//   rulesHeading: {
//     fontSize: 5.5,
//     color: COLORS.purple,
//     fontWeight: "bold",
//     textAlign: "center",
//     marginBottom: 3,
//   },

//   rule: {
//     fontSize: 4.8,
//     color: COLORS.black,
//     marginBottom: 2.2,
//     lineHeight: 1.2,
//     fontWeight: "bold",
//   },

//   /* -------------------------------------------------------
//      EMERGENCY
//   ------------------------------------------------------- */

//   emergencyBox: {
//     marginTop: 5,
//     backgroundColor: COLORS.purple,
//     borderRadius: 4,
//     paddingVertical: 4,
//     paddingHorizontal: 5,
//     alignItems: "center",
//   },

//   emergency: {
//     textAlign: "center",
//     fontSize: 5.2,
//     fontWeight: "bold",
//     color: COLORS.brightGold,
//   },

//   emergencySubtext: {
//     textAlign: "center",
//     fontSize: 4.4,
//     color: COLORS.white,
//     marginTop: 1,
//     fontWeight: "bold",
//   },

//   /* -------------------------------------------------------
//      SIGNATURES
//   ------------------------------------------------------- */

//   signatureSection: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "flex-end",
//     marginTop: 6,
//   },

//   signatureBlock: {
//     width: "42%",
//     alignItems: "center",
//   },

//   signature: {
//     width: "100%",
//     borderTop: `1pt solid ${COLORS.purple}`,
//     paddingTop: 2,
//     textAlign: "center",
//     fontSize: 4.5,
//     color: COLORS.purple,
//     fontWeight: "bold",
//   },

//   /* -------------------------------------------------------
//      BACK FOOTER
//   ------------------------------------------------------- */

//   backFooter: {
//     marginTop: 5,
//     paddingTop: 4,
//     borderTop: `1pt solid ${COLORS.gold}`,
//     alignItems: "center",
//   },

//   backFooterText: {
//     fontSize: 4.4,
//     color: COLORS.black,
//     fontWeight: "bold",
//     textAlign: "center",
//   },
// });

// /* =========================================================
//    IMAGE HANDLER
// ========================================================= */

// const getImageUrl = (image) => {
//   if (!image) {
//     return null;
//   }

//   return image;
// };

// /* =========================================================
//    FRONT CARD
// ========================================================= */

// const StudentFrontCard = ({ student }) => {
//   return (
//     <View style={styles.card}>

//       {/* Decorative Elements */}
//       <View style={styles.topGoldBar} />
//       <View style={styles.topPurpleBar} />
//       <View style={styles.bottomPurpleBar} />
//       <View style={styles.bottomGoldBar} />

//       <View style={styles.cornerGold} />
//       <View style={styles.cornerPurple} />

//       {/* Header */}

//       <View style={styles.header}>

//         <Text style={styles.schoolName}>
//           Salvation In Christ School System
//         </Text>

//         <Text style={styles.schoolAddress}>
//           1st Cowfield Junction, Paynesville City, Monrovia, Liberia
//         </Text>

//         <Text style={styles.schoolSubtitle}>
//           STUDENT IDENTIFICATION CARD
//         </Text>

//         <View style={styles.headerGoldLine} />

//       </View>

//       {/* Main Body */}

//       <View style={styles.cardBody}>

//         {/* Student Photo */}

//         <View style={styles.imageSection}>

//           <View style={styles.imageContainer}>

//             <View style={styles.imageTopAccent} />

//             {getImageUrl(student.student_image) ? (
//               <Image
//                 src={getImageUrl(student.student_image)}
//                 style={styles.studentImage}
//               />
//             ) : (
//               <Text style={styles.noImage}>
//                 NO PHOTO
//               </Text>
//             )}

//           </View>

//           <Text style={styles.photoLabel}>
//             STUDENT
//           </Text>

//         </View>

//         {/* Student Information */}

//         <View style={styles.details}>

//           <View style={styles.studentIdBox}>

//             <Text style={styles.studentIdLabel}>
//               STUDENT IDENTIFICATION NUMBER
//             </Text>

//             <Text style={styles.studentId}>
//               {student.card_number || "STUDENT ID"}
//             </Text>

//           </View>

//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               NAME:
//             </Text>

//             <Text style={styles.value}>
//               {student.student_name || "N/A"}
//             </Text>

//           </View>

//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               CLASS:
//             </Text>

//             <Text style={styles.value}>
//               {student.grade_class || "N/A"}
//             </Text>

//           </View>

//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               DIVISION:
//             </Text>

//             <Text style={styles.value}>
//               {student.division || "N/A"}
//             </Text>

//           </View>

//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               GENDER:
//             </Text>

//             <Text style={styles.value}>
//               {student.gender || "N/A"}
//             </Text>

//           </View>

//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               YEAR:
//             </Text>

//             <Text style={styles.value}>
//               {student.academic_year_name || "N/A"}
//             </Text>

//           </View>

//         </View>

//       </View>

//       {/* Footer */}

//       <View style={styles.footer}>

//         <Text style={styles.footerText}>
//           PROPERTY OF SALVATION IN CHRIST SCHOOL SYSTEM
//         </Text>

//         <Text style={styles.footerYear}>
//           VALID SCHOOL YEAR
//         </Text>

//         <View style={styles.footerGoldAccent} />

//       </View>

//     </View>
//   );
// };

// /* =========================================================
//    CREATE 9 CARD SLOTS
// ========================================================= */

// const createNineSlots = (students) => {
//   const slots = [...students];

//   while (slots.length < 9) {
//     slots.push(null);
//   }

//   return slots.slice(0, 9);
// };

// /* =========================================================
//    BACK CARD
// ========================================================= */

// const StudentBackCard = ({ student }) => {
//   return (
//     <View style={styles.backCard}>

//       {/* Decorative Elements */}

//       <View style={styles.topGoldBar} />
//       <View style={styles.topPurpleBar} />

//       <View style={styles.cornerGold} />
//       <View style={styles.cornerPurple} />

//       {/* Header */}

//       <View style={styles.backTop}>

//         <Text style={styles.backTitle}>
//           SALVATION IN CHRIST SCHOOL SYSTEM
//         </Text>

//         <Text style={styles.backAddress}>
//           1st Cowfield Junction, Paynesville City, Monrovia, Liberia
//         </Text>

//         <Text style={styles.backSubtitle}>
//           STUDENT IDENTIFICATION CARD
//         </Text>

//         <View style={styles.backGoldLine} />

//       </View>

//       {/* Content */}

//       <View style={styles.backContent}>

//         {/* Return Instructions */}

//         <View style={styles.instructionBox}>

//           <Text style={styles.instructionHeading}>
//             IMPORTANT NOTICE
//           </Text>

//           <Text style={styles.instructions}>
//             This identification card remains the property of
//             Salvation In Christ School System. If found, please
//             return it to the school administration or the nearest
//             Police Station.
//           </Text>

//         </View>

//         {/* Rules */}

//         <View style={styles.rulesBox}>

//           <Text style={styles.rulesHeading}>
//             CARD REGULATIONS
//           </Text>

//           <Text style={styles.rule}>
//             • This card must be presented when requested by school officials.
//           </Text>

//           <Text style={styles.rule}>
//             • This card is valid only for the academic year shown on the front.
//           </Text>

//           <Text style={styles.rule}>
//             • Do not lend this card to another person.
//           </Text>

//           <Text style={styles.rule}>
//             • Report lost or damaged cards immediately to the school administration.
//           </Text>

//         </View>

//         {/* Emergency */}

//         <View style={styles.emergencyBox}>

//           <Text style={styles.emergency}>
//             EMERGENCY CONTACT
//           </Text>

//           <Text style={styles.emergencySubtext}>
//             SCHOOL ADMINISTRATION
//           </Text>

//         </View>

//         {/* Signatures */}

//         <View style={styles.signatureSection}>

//           <View style={styles.signatureBlock}>

//             <Text style={styles.signature}>
//               AUTHORIZED SIGNATURE
//             </Text>

//           </View>

//           <View style={styles.signatureBlock}>

//             <Text style={styles.signature}>
//               SCHOOL STAMP
//             </Text>

//           </View>

//         </View>

//         {/* Footer */}

//         <View style={styles.backFooter}>

//           <Text style={styles.backFooterText}>
//             CHRIST-CENTERED EDUCATION • CHARACTER • EXCELLENCE • SERVICE
//           </Text>

//         </View>

//       </View>

//     </View>
//   );
// };

// /* =========================================================
//    MAIN PDF COMPONENT
// ========================================================= */

// const StudentIDCardPDF = ({
//   students = [],
//   academicYear = null,
// }) => {

//   const pages = [];

//   for (
//     let index = 0;
//     index < students.length;
//     index += 9
//   ) {
//     pages.push(
//       students.slice(index, index + 9)
//     );
//   }

//   return (
//     <Document
//       title="Student Identification Cards"
//       author="Salvation In Christ School System"
//       subject="Student Identification Cards"
//     >

//       {pages.map((pageStudents, pageIndex) => {

//         const slots = createNineSlots(pageStudents);

//         return (
//           <React.Fragment
//             key={`card-sheet-${pageIndex}`}
//           >

//             {/* =================================================
//                 FRONT SIDE
//             ================================================= */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >

//               <View style={styles.grid}>

//                 {slots.map((student, index) => (

//                   <View
//                     key={`front-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >

//                     {student && (
//                       <StudentFrontCard
//                         student={student}
//                       />
//                     )}

//                   </View>

//                 ))}

//               </View>

//             </Page>

//             {/* =================================================
//                 BACK SIDE
//             ================================================= */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >

//               <View style={styles.grid}>

//                 {slots.map((student, index) => (

//                   <View
//                     key={`back-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >

//                     {student && (
//                       <StudentBackCard
//                         student={student}
//                       />
//                     )}

//                   </View>

//                 ))}

//               </View>

//             </Page>

//           </React.Fragment>
//         );

//       })}

//     </Document>
//   );
// };

// export default StudentIDCardPDF;










// import React from "react";

// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
// } from "@react-pdf/renderer";


// const styles = StyleSheet.create({

//   page: {
//     size: "A4",
//     orientation: "landscape",
//     padding: 18,
//     backgroundColor: "#ffffff",
//   },


//   grid: {
//     display: "flex",
//     flexDirection: "row",
//     flexWrap: "wrap",
//     justifyContent: "space-between",
//     alignContent: "space-between",
//     height: "100%",
//   },


//   cardWrapper: {
//     width: "31.7%",
//     height: "31.2%",
//     marginBottom: 8,
//   },


//   card: {
//     width: "100%",
//     height: "100%",
//     border: "1pt solid #3F246B",
//     borderRadius: 6,
//     overflow: "hidden",
//     backgroundColor: "#ffffff",
//     position: "relative",
//   },


//   /*
//   |--------------------------------------------------------------------------
//   | DECORATIVE OVERLAPPING CIRCLES
//   |--------------------------------------------------------------------------
//   */

//   circleYellow: {
//     position: "absolute",
//     width: 70,
//     height: 70,
//     borderRadius: 35,
//     backgroundColor: "#F4C430",
//     opacity: 0.22,
//     top: -30,
//     right: -18,
//   },


//   circlePurple: {
//     position: "absolute",
//     width: 78,
//     height: 78,
//     borderRadius: 39,
//     backgroundColor: "#6D3FA9",
//     opacity: 0.20,
//     top: -22,
//     right: 15,
//   },


//   circleGray: {
//     position: "absolute",
//     width: 58,
//     height: 58,
//     borderRadius: 29,
//     backgroundColor: "#7A7A7A",
//     opacity: 0.16,
//     top: 12,
//     right: -18,
//   },


//   bottomCircleYellow: {
//     position: "absolute",
//     width: 80,
//     height: 80,
//     borderRadius: 40,
//     backgroundColor: "#F4C430",
//     opacity: 0.10,
//     bottom: -45,
//     left: -30,
//   },


//   bottomCirclePurple: {
//     position: "absolute",
//     width: 70,
//     height: 70,
//     borderRadius: 35,
//     backgroundColor: "#6D3FA9",
//     opacity: 0.09,
//     bottom: -35,
//     left: 20,
//   },


//   /*
//   |--------------------------------------------------------------------------
//   | FRONT HEADER
//   |--------------------------------------------------------------------------
//   */

//   header: {
//     height: 42,
//     backgroundColor: "#4B2675",
//     paddingHorizontal: 8,
//     paddingVertical: 4,
//     alignItems: "center",
//     justifyContent: "center",
//     position: "relative",
//   },


//   headerAccent: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: "#F4C430",
//   },


//   schoolName: {
//     color: "#FFFFFF",
//     fontSize: 8.5,
//     fontWeight: "bold",
//     textAlign: "center",
//     textTransform: "uppercase",
//     letterSpacing: 0.4,
//   },


//   schoolAddress: {
//     color: "#FFFFFF",
//     fontSize: 4.8,
//     marginTop: 1,
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   schoolSubtitle: {
//     color: "#F8D85A",
//     fontSize: 5.2,
//     marginTop: 1,
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   /*
//   |--------------------------------------------------------------------------
//   | CARD BODY
//   |--------------------------------------------------------------------------
//   */

//   cardBody: {
//     flexDirection: "row",
//     padding: 6,
//     flex: 1,
//     position: "relative",
//   },


//   imageContainer: {
//     width: 62,
//     height: 78,
//     border: "1.2pt solid #4B2675",
//     borderRadius: 4,
//     overflow: "hidden",
//     alignItems: "center",
//     justifyContent: "center",
//     backgroundColor: "#F1F1F1",
//     position: "relative",
//   },


//   imageAccent: {
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: 4,
//     backgroundColor: "#F4C430",
//   },


//   studentImage: {
//     width: "100%",
//     height: "100%",
//     objectFit: "cover",
//   },


//   noImage: {
//     fontSize: 6,
//     color: "#555555",
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   details: {
//     flex: 1,
//     marginLeft: 7,
//     justifyContent: "center",
//   },


//   studentId: {
//     fontSize: 7.2,
//     color: "#4B2675",
//     fontWeight: "bold",
//     marginBottom: 4,
//   },


//   idAccent: {
//     width: 35,
//     height: 2,
//     backgroundColor: "#F4C430",
//     marginBottom: 4,
//   },


//   detailRow: {
//     flexDirection: "row",
//     marginBottom: 3,
//   },


//   label: {
//     width: 48,
//     fontSize: 5.5,
//     fontWeight: "bold",
//     color: "#5D5D5D",
//   },


//   value: {
//     flex: 1,
//     fontSize: 6.5,
//     color: "#171717",
//     fontWeight: "bold",
//   },


//   /*
//   |--------------------------------------------------------------------------
//   | FOOTER
//   |--------------------------------------------------------------------------
//   */

//   footer: {
//     height: 17,
//     backgroundColor: "#F7F4FB",
//     borderTop: "0.5pt solid #D7CBE5",
//     paddingHorizontal: 6,
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//   },


//   footerText: {
//     fontSize: 4.8,
//     color: "#4B2675",
//     fontWeight: "bold",
//   },


//   footerAccent: {
//     position: "absolute",
//     left: 0,
//     bottom: 0,
//     width: "35%",
//     height: 2,
//     backgroundColor: "#F4C430",
//   },


//   /*
//   |--------------------------------------------------------------------------
//   | BACK CARD
//   |--------------------------------------------------------------------------
//   */

//   backCard: {
//     width: "100%",
//     height: "100%",
//     border: "1pt solid #3F246B",
//     borderRadius: 6,
//     overflow: "hidden",
//     backgroundColor: "#FFFFFF",
//     position: "relative",
//   },


//   backTop: {
//     height: 42,
//     backgroundColor: "#4B2675",
//     alignItems: "center",
//     justifyContent: "center",
//     position: "relative",
//   },


//   backTopAccent: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: "#F4C430",
//   },


//   backTitle: {
//     color: "#FFFFFF",
//     fontSize: 8.5,
//     fontWeight: "bold",
//     textAlign: "center",
//   },


//   backAddress: {
//     color: "#FFFFFF",
//     fontSize: 4.8,
//     marginTop: 1,
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   backSubtitle: {
//     color: "#F8D85A",
//     fontSize: 5.2,
//     marginTop: 1,
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   backContent: {
//     flex: 1,
//     padding: 8,
//     justifyContent: "space-between",
//     position: "relative",
//   },


//   backCircleYellow: {
//     position: "absolute",
//     width: 80,
//     height: 80,
//     borderRadius: 40,
//     backgroundColor: "#F4C430",
//     opacity: 0.10,
//     top: -30,
//     right: -30,
//   },


//   backCirclePurple: {
//     position: "absolute",
//     width: 65,
//     height: 65,
//     borderRadius: 32.5,
//     backgroundColor: "#6D3FA9",
//     opacity: 0.10,
//     top: -15,
//     right: 20,
//   },


//   backCircleGray: {
//     position: "absolute",
//     width: 50,
//     height: 50,
//     borderRadius: 25,
//     backgroundColor: "#777777",
//     opacity: 0.09,
//     top: 10,
//     right: -12,
//   },


//   instructions: {
//     fontSize: 6.3,
//     lineHeight: 1.45,
//     color: "#202020",
//     textAlign: "center",
//     fontWeight: "bold",
//   },


//   rulesBox: {
//     border: "0.7pt solid #D4C8E2",
//     padding: 6,
//     marginTop: 6,
//     borderRadius: 4,
//     backgroundColor: "#FAF8FD",
//   },


//   rule: {
//     fontSize: 5.4,
//     color: "#333333",
//     marginBottom: 2,
//   },


//   emergency: {
//     textAlign: "center",
//     fontSize: 6,
//     fontWeight: "bold",
//     color: "#4B2675",
//     marginTop: 5,
//   },


//   emergencyAccent: {
//     width: 70,
//     height: 2,
//     backgroundColor: "#F4C430",
//     alignSelf: "center",
//     marginTop: 3,
//   },


//   signatureSection: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "flex-end",
//     marginTop: 6,
//   },


//   signature: {
//     width: "42%",
//     borderTop: "0.6pt solid #555555",
//     paddingTop: 2,
//     textAlign: "center",
//     fontSize: 5,
//     color: "#555555",
//     fontWeight: "bold",
//   },

// });


// const getImageUrl = (image) => {

//   if (!image) {
//     return null;
//   }

//   return image;

// };


// const StudentFrontCard = ({ student }) => {

//   return (

//     <View style={styles.card}>

//       {/* DECORATIVE OVERLAPPING CIRCLES */}

//       <View style={styles.circleYellow} />

//       <View style={styles.circlePurple} />

//       <View style={styles.circleGray} />

//       <View style={styles.bottomCircleYellow} />

//       <View style={styles.bottomCirclePurple} />


//       {/* FRONT HEADER */}

//       <View style={styles.header}>

//         <Text style={styles.schoolName}>
//           SALVATION IN CHRIST SCHOOL SYSTEM
//         </Text>

//         <Text style={styles.schoolAddress}>
//           PAYNESVILLE, MONTSERRADO COUNTY, LIBERIA
//         </Text>

//         <Text style={styles.schoolSubtitle}>
//           STUDENT IDENTIFICATION CARD
//         </Text>

//         <View style={styles.headerAccent} />

//       </View>


//       {/* CARD BODY */}

//       <View style={styles.cardBody}>

//         <View style={styles.imageContainer}>

//           <View style={styles.imageAccent} />

//           {getImageUrl(student.student_image) ? (

//             <Image
//               src={getImageUrl(student.student_image)}
//               style={styles.studentImage}
//             />

//           ) : (

//             <Text style={styles.noImage}>
//               NO PHOTO
//             </Text>

//           )}

//         </View>


//         <View style={styles.details}>

//           <Text style={styles.studentId}>
//             {student.card_number || "STUDENT ID"}
//           </Text>

//           <View style={styles.idAccent} />


//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               NAME:
//             </Text>

//             <Text style={styles.value}>
//               {student.student_name || "N/A"}
//             </Text>

//           </View>


//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               CLASS:
//             </Text>

//             <Text style={styles.value}>
//               {student.grade_class || "N/A"}
//             </Text>

//           </View>


//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               DIVISION:
//             </Text>

//             <Text style={styles.value}>
//               {student.division || "N/A"}
//             </Text>

//           </View>


//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               GENDER:
//             </Text>

//             <Text style={styles.value}>
//               {student.gender || "N/A"}
//             </Text>

//           </View>


//           <View style={styles.detailRow}>

//             <Text style={styles.label}>
//               YEAR:
//             </Text>

//             <Text style={styles.value}>
//               {student.academic_year_name || "N/A"}
//             </Text>

//           </View>

//         </View>

//       </View>


//       {/* FRONT FOOTER */}

//       <View style={styles.footer}>

//         <Text style={styles.footerText}>
//           PROPERTY OF SALVATION IN CHRIST SCHOOL SYSTEM
//         </Text>

//         <Text style={styles.footerText}>
//           VALID SCHOOL YEAR
//         </Text>

//         <View style={styles.footerAccent} />

//       </View>

//     </View>

//   );

// };


// const StudentBackCard = ({ student }) => {

//   return (

//     <View style={styles.backCard}>

//       {/* DECORATIVE BACKGROUND CIRCLES */}

//       <View style={styles.backCircleYellow} />

//       <View style={styles.backCirclePurple} />

//       <View style={styles.backCircleGray} />


//       {/* BACK HEADER */}

//       <View style={styles.backTop}>

//         <Text style={styles.backTitle}>
//           SALVATION IN CHRIST SCHOOL SYSTEM
//         </Text>

//         <Text style={styles.backAddress}>
//           PAYNESVILLE, MONTSERRADO COUNTY, LIBERIA
//         </Text>

//         <Text style={styles.backSubtitle}>
//           STUDENT IDENTIFICATION CARD
//         </Text>

//         <View style={styles.backTopAccent} />

//       </View>


//       {/* BACK CONTENT */}

//       <View style={styles.backContent}>

//         <Text style={styles.instructions}>

//           This identification card remains the property of
//           Jana L. Williams Educational Foundation. If found, please
//           return it to the school address or the nearest Police Station.

//         </Text>


//         <View style={styles.rulesBox}>

//           <Text style={styles.rule}>
//             • This card must be presented when requested by school officials.
//           </Text>

//           <Text style={styles.rule}>
//             • The card is valid only for the academic year shown on the front.
//           </Text>

//           <Text style={styles.rule}>
//             • Do not lend this card to another person.
//           </Text>

//           <Text style={styles.rule}>
//             • Report a lost or damaged card to the administration immediately.
//           </Text>

//         </View>


//         <View>

//           <Text style={styles.emergency}>
//             EMERGENCY CONTACT: SCHOOL ADMINISTRATION
//           </Text>

//           <View style={styles.emergencyAccent} />

//         </View>


//         <View style={styles.signatureSection}>

//           <Text style={styles.signature}>
//             AUTHORIZED SIGNATURE
//           </Text>

//           <Text style={styles.signature}>
//             SCHOOL STAMP
//           </Text>

//         </View>

//       </View>

//     </View>

//   );

// };


// const createNineSlots = (students) => {

//   const slots = [...students];


//   while (slots.length < 9) {
//     slots.push(null);
//   }


//   return slots.slice(0, 9);

// };


// const StudentIDCardPDF = ({
//   students = [],
//   academicYear = null,
// }) => {

//   const pages = [];


//   for (
//     let index = 0;
//     index < students.length;
//     index += 9
//   ) {

//     pages.push(
//       students.slice(index, index + 9)
//     );

//   }


//   return (

//     // <Document
//     //   title="Student Identification Cards"
//     //   author="Salvation In Christ School System"
//     //   subject="Student Identification Cards"
//     // >
//         <Document
//       title="Student Identification Cards"
//       author="Jana L. Williams Educational Foundation"
//       subject="Student Identification Cards"
//     >

//       {pages.map((pageStudents, pageIndex) => {

//         const slots = createNineSlots(pageStudents);


//         return (

//           <React.Fragment
//             key={`card-sheet-${pageIndex}`}
//           >

//             {/* FRONT OF CARDS */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >

//               <View style={styles.grid}>

//                 {slots.map((student, index) => (

//                   <View
//                     key={`front-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >

//                     {student ? (

//                       <StudentFrontCard
//                         student={student}
//                       />

//                     ) : null}

//                   </View>

//                 ))}

//               </View>

//             </Page>


//             {/* BACK OF CARDS */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >

//               <View style={styles.grid}>

//                 {slots.map((student, index) => (

//                   <View
//                     key={`back-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >

//                     {student ? (

//                       <StudentBackCard
//                         student={student}
//                       />

//                     ) : null}

//                   </View>

//                 ))}

//               </View>

//             </Page>

//           </React.Fragment>

//         );

//       })}

//     </Document>

//   );

// };


// export default StudentIDCardPDF;
